// src/pages/Signup.jsx

import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import notify from '../lib/notify';
import { SITE_URL } from "../lib/config";
import { trackSignup, trackReferralSignup } from '../hooks/useAnalytics';
import { Button as FcButton, Card as FcCard, Input as FcInput } from '../ui';
import { Dialog as FcDialog } from '../ui';

export default function Signup({ session, userRole, username }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();
  
  // Capture referral code from URL parameter (?ref=CODE) or localStorage
  const [searchParams] = useSearchParams();
  const [referralCode] = useState(
    searchParams.get('ref') || localStorage.getItem('pendingReferral') || ''
  );

  // Check for pending join intent
  useEffect(() => {
    const pendingJoin = localStorage.getItem('pendingJoin');
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      notify('Please agree to the Terms of Service and Privacy Policy', 'error');
      return;
    }
    
    setLoading(true);

    try {
      // Create user account with referral code stored in metadata
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${SITE_URL}`,
          data: {
            referral_code: referralCode?.trim() || null,
          },
        },
      });

      if (authError) throw authError;

      // Track signup event
      trackSignup('email');
      if (referralCode?.trim()) {
        trackReferralSignup(referralCode.trim());
      }

      // Show verification modal instead of navigating to login
      setShowVerificationModal(true);
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-canvas flex flex-col">
      {/* Green ribbon header */}
      <div className="w-full bg-ui-green py-5 px-6 flex items-center">
        <span className="text-white text-xl font-semibold">Footy Coach</span>
      </div>
      <div className="flex flex-1 items-center justify-center">
        <div className="w-full max-w-4xl rounded-lg shadow-md flex flex-col md:flex-row overflow-hidden m-4">
          {/* Left: FC Logo (desktop only) */}
          <div className="hidden md:flex flex-1 items-center justify-center bg-action-primary">
            <span className="text-[10vw] font-bold text-ui-green select-none">FC</span>
          </div>
          {/* Right: Signup Form */}
          <div role="main" id="fc-main-content" className="flex-1 bg-surface-card flex flex-col justify-center p-8">
            <h1 className="text-3xl font-bold text-black mb-8 text-center md:text-left">Sign Up</h1>
            <form onSubmit={handleSignup} className="space-y-6">
              <div>
                <label htmlFor="email" className="block font-semibold mb-1 text-black">Email</label>
                <FcInput
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border"
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="block font-semibold mb-1 text-black">Password</label>
                <FcInput
                  id="password"
                  autoComplete="new-password"
                  aria-describedby="signup-password-requirements"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border"
                  required
                />
                <p id="signup-password-requirements" className="text-sm text-content-secondary mt-2">
                  Use at least 10 characters. Include at least one uppercase letter, one lowercase letter, one number and one special character.
                </p>
              </div>
              
              {/* Referral input is temporarily hidden; existing link attribution is preserved. */}
              {/* Terms and Privacy Agreement */}
              <div className="flex items-start gap-3">
                <FcInput
                  id="terms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4"
                  required
                />
                <label htmlFor="terms" className="text-sm text-content-primary">
                  I agree to the{' '}
                  <Link to="/terms" className="text-action-primary hover:underline font-semibold" target="_blank">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-action-primary hover:underline font-semibold" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
              </div>
              
              <FcButton variant="primary"
                type="submit"
                className="w-full disabled:cursor-not-allowed"
                disabled={loading || !agreedToTerms}
              >
                {loading ? "Signing Up..." : "Sign Up"}
              </FcButton>
            </form>
            <div className="mt-6 text-center text-black text-base">
              <span>Already have an account? </span>
              <Link to="/login" className="text-action-primary font-semibold hover:underline">Login here</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <FcDialog open title="Verify Your Email" size="standard" onCancel={() => setShowVerificationModal(false)}>
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mb-4 text-5xl">📧</div>

              <p className="text-content-primary mb-2">
                We've sent a verification email to:
              </p>
              <p className="font-semibold text-action-primary mb-4">{email}</p>
              <p className="text-content-secondary mb-6">
                Please check your inbox and click the verification link to activate your account.
              </p>
              <FcButton variant="primary" type="submit"
                onClick={() => navigate("/login")}
                className="w-full"
              >
                Go to Login
              </FcButton>
              <p className="text-sm text-content-secondary mt-4">
                Didn't receive the email? Check your spam folder.
              </p>
            </div>
          </FcCard>
        </FcDialog>
      )}
    </div>
  );
}
